import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap, catchError } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private translations: any = {};
  private currentLang = 'en';
  private isLoading = false;
  private isLoaded = false;
  public langChange = new BehaviorSubject<string>(this.currentLang);

  constructor(private http: HttpClient, private ngxTranslate: TranslateService) {}

  loadLanguage(namespace?: string): Promise<void> {
    const lang = localStorage.getItem('reseller_portal_language') || this.currentLang;
    
    this.isLoading = true;
    this.isLoaded = false;
    
    // Add cache-busting parameter to force fresh translation file load
    // Using timestamp + random to ensure no caching
    const cacheBuster = `${new Date().getTime()}.${Math.random()}`;
    return this.http.get(`/assets/i18n/${lang}.json?v=${cacheBuster}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    })
      .pipe(
        catchError(err => {
          console.error(`Error loading ${lang} translations:`, err);
          this.isLoading = false;
          throw err;
        })
      )
      .toPromise()
      .then(data => {
        console.log('[TranslationService] Loaded translations for', lang, ':', data);
        this.translations = data;
        this.currentLang = lang;
        this.isLoading = false;
        this.isLoaded = true;
        localStorage.setItem('reseller_portal_language', lang);

        // The auth screens call ngx-translate's TranslateService.instant() directly,
        // so hand it the same JSON or those messages render as raw keys.
        this.ngxTranslate.setTranslation(lang, data);
        this.ngxTranslate.use(lang);

        this.langChange.next(lang); // Notify subscribers
      });
  }

  setLanguage(lang: string): void {
    this.currentLang = lang;
    localStorage.setItem('reseller_portal_language', lang);
    this.loadLanguage().then(() => {
      this.langChange.next(lang);
    });
  }

  translate(key: string, params: any = {}): string {
    if (!this.translations || !key) {
      return key;
    }

    const keys = key.split('.');
    let value = this.translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Only log warning if translations are fully loaded (not during initial load)
        if (this.isLoaded && !this.isLoading) {
          console.warn(`Translation key not found: ${key} for language ${this.currentLang}`);
        }
        return key;
      }
    }

    const result = typeof value === 'string' ? value : key;
    
    if (params) {
      return Object.keys(params).reduce((text, param) => {
        return text.replace(new RegExp(`\\{\\{\\s*${param}\\s*\\}\\}`, 'g'), params[param]);
      }, result);
    }
    
    return result;
  }

  getCurrentLang() {
    return this.currentLang;
  }

  // Compatibility methods for existing code
  loadTranslations(...args: any[]): void {
    // Legacy method - no-op since we load from JSON files
    console.log('loadTranslations called (legacy method - using JSON files instead)');
  }

  getSelectedLanguage() {
    return this.langChange.asObservable();
  }
}

